/**
 * How field completion documents are produced for an activity type.
 * Set under Einstellungen → Aktivitäten; resolved from activity_type (no per-activity override).
 */
export const ACTIVITY_DOCUMENT_COMPLETION_MODES = [
  "checklist_only",
  "report_with_checklist",
  "report_and_checklist_separate",
  "report_only",
] as const;

export type ActivityDocumentCompletionMode = (typeof ACTIVITY_DOCUMENT_COMPLETION_MODES)[number];

export const DEFAULT_ACTIVITY_DOCUMENT_COMPLETION_MODE: ActivityDocumentCompletionMode =
  "report_and_checklist_separate";

export const ACTIVITY_DOCUMENT_COMPLETION_MODE_OPTIONS: ReadonlyArray<{
  value: ActivityDocumentCompletionMode;
  label: string;
}> = [
  { value: "checklist_only", label: "Nur Checkliste" },
  { value: "report_with_checklist", label: "Arbeitsbericht inkl. Checkliste" },
  { value: "report_and_checklist_separate", label: "Arbeitsbericht + Checkliste getrennt" },
  { value: "report_only", label: "Nur Arbeitsbericht" },
];

export function isActivityDocumentCompletionMode(
  value: unknown,
): value is ActivityDocumentCompletionMode {
  return (
    typeof value === "string" &&
    (ACTIVITY_DOCUMENT_COMPLETION_MODES as readonly string[]).includes(value)
  );
}

export function parseActivityDocumentCompletionMode(
  value: unknown,
): ActivityDocumentCompletionMode {
  return isActivityDocumentCompletionMode(value)
    ? value
    : DEFAULT_ACTIVITY_DOCUMENT_COMPLETION_MODE;
}

/** Work-report PDF is created (possibly with or without checklist). */
export function documentCompletionIncludesWorkReport(
  mode: ActivityDocumentCompletionMode,
): boolean {
  return mode !== "checklist_only";
}

/** Checklist answers are collected and a protocol PDF is produced (when a checklist is assigned). */
export function documentCompletionIncludesChecklist(
  mode: ActivityDocumentCompletionMode,
): boolean {
  return mode !== "report_only";
}

/** Checklist protocol is stored as its own lead document (not merged into the work report). */
export function documentCompletionKeepsChecklistSeparate(
  mode: ActivityDocumentCompletionMode,
): boolean {
  return mode === "report_and_checklist_separate" || mode === "checklist_only";
}

/**
 * Whether `POST .../work-report` processes checklist answers for this mode.
 * "Getrennt": the app sends the checklist via `/checklist-protocol`; the work report
 * only carries answers when an older build still runs the combined wizard, so the
 * checklist is validated and stored only if answers are part of the request.
 */
export function workReportProcessesChecklist(
  mode: ActivityDocumentCompletionMode,
  hasChecklistAnswersInBody: boolean,
): boolean {
  if (!documentCompletionIncludesChecklist(mode)) return false;
  if (mode === "report_and_checklist_separate") return hasChecklistAnswersInBody;
  return true;
}
